import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../Logo';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../lib/supabase';
import { ThemeToggle } from '../ThemeToggle';
import { IconAssets, IconBell, IconCog, IconDocument, IconDownload, IconHome, IconMomoShield } from './NavIcons';
import {
  cancelAccountDeletion,
  expireTrials,
  getAssetBillingStatus,
  getLockedAlertsCount,
  getMonitoringStatus,
  type AssetBillingStatusItem,
  type MonitoringStatus,
} from '../../server/api';
import OnboardingWizard, { type OnboardingCompleteInfo } from '../OnboardingWizard';
import { SessionTimeoutModal } from '../SessionTimeoutModal';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import { FeatureGate } from '../FeatureGate';
import { useUserContext } from '../../hooks/useUserContext';
import { useCart } from '../../hooks/useCart';

const DELETION_ACK_KEY = 'deletion_acknowledged';
const TRIAL_BANNER_KEY = 'trial_banner_dismissed';
const TRIAL_EXPIRY_DISMISS_KEY = 'trial_expiry_dismissed';
const GRACE_BANNER_KEY = 'grace_banner_dismissed';
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;
const SESSION_WARNING_MS = 2 * 60 * 1000;
const INACTIVITY_LOGOUT_MESSAGE = 'You were logged out due to inactivity.';

type ToastItem = { id: number; message: string; variant: 'success' | 'error' };

export type AssetBillingOutletContext = {
  expiredAssetRows: AssetBillingStatusItem[];
  trialAssetRows: AssetBillingStatusItem[];
};

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Days from today until deletion date (calendar days). */
function calendarDaysUntilDeletion(iso: string): number {
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return 0;
  const diff = startOfDay(end) - startOfDay(new Date());
  return Math.round(diff / 86400000);
}

function formatDeletionDisplayDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { dateStyle: 'long' });
}

function deletionBannerCopy(iso: string): string {
  const dateStr = formatDeletionDisplayDate(iso);
  const days = calendarDaysUntilDeletion(iso);
  if (days <= 0) {
    return `Your account will be permanently deleted today (${dateStr}). `;
  }
  if (days === 1) {
    return `Your account will be permanently deleted tomorrow (${dateStr}). `;
  }
  return `Your account will be permanently deleted in ${days} days on ${dateStr}. `;
}

function IconMarketplaceBag({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l-1 12H6L5 9z"
      />
    </svg>
  );
}

function IconNetworkNav({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3L4 6v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V6l-8-3z"
      />
      <circle cx="8" cy="12" r="1.25" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.25" fill="currentColor" stroke="none" />
      <circle cx="16" cy="12" r="1.25" fill="currentColor" stroke="none" />
      <path strokeLinecap="round" strokeWidth={1.5} d="M8 12h8" />
    </svg>
  );
}

function NavBarTooltip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="nav-tip-parent relative inline-flex">
      {children}
      <span className="nav-icon-tooltip" role="tooltip">
        <span className="nav-icon-tooltip-caret" aria-hidden />
        {label}
      </span>
    </span>
  );
}

function pageTitle(pathname: string): string {
  if (pathname.startsWith('/dashboard')) return 'Dashboard';
  if (pathname.startsWith('/alerts')) return 'Alerts';
  if (pathname.startsWith('/assets')) return 'Assets';
  if (pathname.startsWith('/download')) return 'Download Agent';
  if (pathname.startsWith('/momo')) return 'MoMo Protection';
  if (pathname.startsWith('/network')) return 'Network Monitor';
  if (pathname.startsWith('/reports/april-2026')) return 'April 2026 Security Summary';
  if (pathname.startsWith('/reports')) return 'Reports';
  if (pathname.startsWith('/checkout')) return 'Checkout';
  if (pathname.startsWith('/marketplace')) return 'Marketplace';
  if (pathname.startsWith('/settings/support-access')) return 'Support access';
  if (pathname.startsWith('/settings')) return 'Settings';
  return 'ShieldSME';
}

function avatarInitials(ownerName: string | undefined, email: string | undefined): string {
  const name = (ownerName ?? '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  const em = (email ?? '').trim();
  if (em) return em.slice(0, 2).toUpperCase();
  return '?';
}

function AvatarCircle({
  url,
  initials,
  className = 'h-9 w-9',
}: {
  url?: string | null;
  initials: string;
  className?: string;
}) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className={`rounded-full object-cover ring-2 ring-border ${className}`}
      />
    );
  }
  return (
    <div
      className={`flex items-center justify-center rounded-full bg-border text-xs font-semibold text-foreground ring-2 ring-border ${className}`}
      aria-hidden
    >
      {initials}
    </div>
  );
}

export function ProtectedAppShell() {
  const {
    currentUser,
    userProfile,
    signOut,
    isDeletionPending,
    deletionScheduledAt,
    refreshProfile,
    loading: authLoading,
    isOnboardingComplete,
  } = useAuth();
  const { isPersonal } = useUserContext();
  const { totalItems, clearCart } = useCart();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const title = pageTitle(location.pathname);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [storageTick, setStorageTick] = useState(0);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [deletionModalError, setDeletionModalError] = useState<string | null>(null);
  const [modalCancelLoading, setModalCancelLoading] = useState(false);
  const [bannerCancelLoading, setBannerCancelLoading] = useState(false);

  const isAdminUser = userProfile?.role?.trim().toLowerCase() === 'admin';
  /** Require loaded profile so admins are not timed out before `role` is known. */
  const sessionTimeoutEnabled = Boolean(
    currentUser && !authLoading && userProfile !== null && !isAdminUser
  );
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [expiredAssetRows, setExpiredAssetRows] = useState<AssetBillingStatusItem[]>([]);
  const [trialAssetRows, setTrialAssetRows] = useState<AssetBillingStatusItem[]>([]);
  const [monitoringStatus, setMonitoringStatus] = useState<MonitoringStatus | null>(null);
  const [lockedAlertCount, setLockedAlertCount] = useState(0);
  const logoutDeadlineRef = useRef(0);
  const idleLogoutLock = useRef(false);
  const performIdleLogoutRef = useRef<() => Promise<void>>(async () => {});
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  useEffect(() => {
    performIdleLogoutRef.current = async () => {
      if (idleLogoutLock.current) return;
      idleLogoutLock.current = true;
      setShowTimeoutWarning(false);
      try {
        console.warn('[security] User session terminated due to inactivity', {
          timestamp: new Date().toISOString(),
          userId: currentUserRef.current?.id,
          reason: 'idle_timeout',
        });
        clearCart();
        await supabase.auth.signOut();
        navigate('/login', {
          replace: true,
          state: { message: INACTIVITY_LOGOUT_MESSAGE },
        });
      } finally {
        idleLogoutLock.current = false;
      }
    };
  }, [navigate, clearCart]);

  const { resetTimer } = useSessionTimeout({
    enabled: sessionTimeoutEnabled,
    timeoutMs: SESSION_TIMEOUT_MS,
    warningMs: SESSION_WARNING_MS,
    onWarn: () => {
      logoutDeadlineRef.current = Date.now() + SESSION_WARNING_MS;
      setSecondsRemaining(Math.max(0, Math.ceil(SESSION_WARNING_MS / 1000)));
      setShowTimeoutWarning(true);
    },
    onLogout: () => {
      void performIdleLogoutRef.current();
    },
    onUserActivity: () => {
      console.info('[security] Session extended by user activity', {
        timestamp: new Date().toISOString(),
      });
      setShowTimeoutWarning(false);
    },
  });

  useEffect(() => {
    if (!showTimeoutWarning) return;
    const tick = () => {
      const s = Math.max(0, Math.ceil((logoutDeadlineRef.current - Date.now()) / 1000));
      setSecondsRemaining(s);
      if (s <= 0) void performIdleLogoutRef.current();
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [showTimeoutWarning]);

  const handleStayLoggedIn = useCallback(() => {
    resetTimer();
    setShowTimeoutWarning(false);
  }, [resetTimer]);

  const handleLogoutNowFromModal = useCallback(() => {
    void performIdleLogoutRef.current();
  }, []);

  // storageTick bumps after sessionStorage mutates so we re-read acknowledgement (void satisfies exhaustive-deps).
  const deletionAcknowledged = (() => {
    void storageTick;
    if (typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(DELETION_ACK_KEY) === 'true';
  })();
  const trialBannerDismissed = (() => {
    void storageTick;
    if (typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(TRIAL_BANNER_KEY) === 'true';
  })();
  const trialExpiryAssetBannerDismissed = (() => {
    void storageTick;
    if (typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(TRIAL_EXPIRY_DISMISS_KEY) === 'true';
  })();
  const graceBannerDismissed = (() => {
    void storageTick;
    if (typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(GRACE_BANNER_KEY) === 'true';
  })();

  const billingAssetSyncEnabled = Boolean(
    currentUser && !authLoading && userProfile !== null
  );

  useEffect(() => {
    if (!billingAssetSyncEnabled) {
      setExpiredAssetRows([]);
      setTrialAssetRows([]);
      setMonitoringStatus(null);
      setLockedAlertCount(0);
      return;
    }
    let cancelled = false;

    const applyAssetRowsFromStatus = (st: Awaited<ReturnType<typeof getAssetBillingStatus>>) => {
      if (!st.ok) {
        setExpiredAssetRows([]);
        setTrialAssetRows([]);
        return;
      }
      const now = Date.now();
      setExpiredAssetRows(st.assets.filter((a) => a.billing_status === 'expired'));
      setTrialAssetRows(
        st.assets.filter((a) => {
          if (!a.is_trial || a.billing_status !== 'trial' || !a.trial_expires_at) return false;
          return new Date(a.trial_expires_at).getTime() > now;
        })
      );
    };

    void (async () => {
      const [st, locked, mon] = await Promise.all([
        getAssetBillingStatus(),
        getLockedAlertsCount(),
        getMonitoringStatus(),
      ]);
      if (cancelled) return;
      if (locked.ok) setLockedAlertCount(locked.count);
      else setLockedAlertCount(0);
      if (mon.ok) setMonitoringStatus(mon.status);
      else setMonitoringStatus(null);
      applyAssetRowsFromStatus(st);
    })();

    const timer = window.setTimeout(() => {
      void (async () => {
        const ex = await expireTrials();
        if (cancelled) return;
        let monStatus: MonitoringStatus | null = null;
        if (ex.ok && ex.monitoring) {
          monStatus = ex.monitoring;
        } else {
          const mon = await getMonitoringStatus();
          if (mon.ok) monStatus = mon.status;
        }
        const [st, locked] = await Promise.all([getAssetBillingStatus(), getLockedAlertsCount()]);
        if (cancelled) return;
        setMonitoringStatus(monStatus);
        if (locked.ok) setLockedAlertCount(locked.count);
        else setLockedAlertCount(0);
        applyAssetRowsFromStatus(st);
      })();
    }, 1000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [billingAssetSyncEnabled, currentUser?.id]);

  const showDeletionBlockerModal = isDeletionPending && !deletionAcknowledged;
  const showDeletionBanner = isDeletionPending && deletionAcknowledged;

  const trialExpiresAt = userProfile?.trial_expires_at ?? null;
  const trialDaysLeft = trialExpiresAt
    ? Math.max(0, Math.ceil((new Date(trialExpiresAt).getTime() - Date.now()) / 86400000))
    : 0;
  const showTrialBanner =
    !authLoading &&
    userProfile !== null &&
    userProfile.is_trial === true &&
    trialExpiresAt !== null &&
    new Date(trialExpiresAt).getTime() > Date.now() &&
    trialBannerDismissed !== true;
  const trialBannerStyle =
    trialDaysLeft >= 8
      ? { backgroundColor: '#059669', color: '#ffffff' }
      : trialDaysLeft >= 3
        ? { backgroundColor: '#F59E0B', color: '#ffffff' }
        : { backgroundColor: '#DC2626', color: '#ffffff' };
  const trialPlanLabel = userProfile?.trial_plan ?? 'starter';

  const showExpiredAssetTrialBanner =
    expiredAssetRows.length > 0 && !trialExpiryAssetBannerDismissed;

  const showGraceBanner =
    monitoringStatus?.hasGraceAssets === true &&
    !graceBannerDismissed &&
    Boolean(monitoringStatus?.graceEndsAt) &&
    Boolean(monitoringStatus?.graceTrialExpiredAt);

  const showSoftStopBanner = monitoringStatus?.hasSoftStoppedAssets === true;

  const graceDaysLeft = monitoringStatus?.graceEndsAt
    ? Math.max(0, Math.ceil((new Date(monitoringStatus.graceEndsAt).getTime() - Date.now()) / 86400000))
    : 0;
  const trialExpiredDaysAgo = monitoringStatus?.graceTrialExpiredAt
    ? Math.max(
        1,
        Math.ceil((Date.now() - new Date(monitoringStatus.graceTrialExpiredAt).getTime()) / 86400000)
      )
    : 1;
  const graceBannerStyle =
    graceDaysLeft <= 2
      ? { backgroundColor: '#DC2626', color: '#ffffff' }
      : { backgroundColor: '#F59E0B', color: '#ffffff' };

  const softStopThreatCount =
    lockedAlertCount > 0 ? lockedAlertCount : (monitoringStatus?.softStoppedCount ?? 0);

  const fixedBannerRowCount =
    (showTrialBanner ? 1 : 0) +
    (showGraceBanner ? 1 : 0) +
    (showSoftStopBanner ? 1 : 0) +
    (showExpiredAssetTrialBanner ? 1 : 0) +
    (showDeletionBanner ? 1 : 0);

  const assetTrialExpiryBannerMessage =
    expiredAssetRows.length === 1
      ? `Your trial for ${expiredAssetRows[0].asset_value} has expired`
      : `${expiredAssetRows.length} monitored assets have expired trials`;

  const outletBillingContext = useMemo<AssetBillingOutletContext>(
    () => ({ expiredAssetRows, trialAssetRows }),
    [expiredAssetRows, trialAssetRows]
  );

  const pushToast = useCallback((message: string, variant: 'success' | 'error') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, variant }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4200);
  }, []);

  useEffect(() => {
    if (!showDeletionBlockerModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDeletionBlockerModal]);

  const handleDeletionCancel = useCallback(
    async (source: 'modal' | 'banner') => {
      if (source === 'modal') {
        setModalCancelLoading(true);
        setDeletionModalError(null);
      } else {
        setBannerCancelLoading(true);
      }
      const result = await cancelAccountDeletion();
      if (source === 'modal') setModalCancelLoading(false);
      else setBannerCancelLoading(false);

      if (!result.ok) {
        if (source === 'modal') setDeletionModalError(result.error);
        else pushToast(result.error, 'error');
        return;
      }
      await refreshProfile();
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(DELETION_ACK_KEY);
      setStorageTick((t) => t + 1);
      pushToast('Your account deletion has been cancelled. Welcome back!', 'success');
    },
    [pushToast, refreshProfile]
  );

  const handleDeletionAcknowledge = useCallback(() => {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(DELETION_ACK_KEY, 'true');
    setStorageTick((t) => t + 1);
  }, []);
  const handleDismissTrialBanner = useCallback(() => {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(TRIAL_BANNER_KEY, 'true');
    setStorageTick((t) => t + 1);
  }, []);

  const handleDismissTrialExpiryAssetBanner = useCallback(() => {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(TRIAL_EXPIRY_DISMISS_KEY, 'true');
    setStorageTick((t) => t + 1);
  }, []);

  const handleDismissGraceBanner = useCallback(() => {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(GRACE_BANNER_KEY, 'true');
    setStorageTick((t) => t + 1);
  }, []);

  const avatarUrl =
    (currentUser?.user_metadata?.avatar_url as string | undefined) ??
    (currentUser?.user_metadata?.picture as string | undefined) ??
    null;
  const initials = avatarInitials(userProfile?.owner_name, currentUser?.email ?? undefined);

  const refreshUnread = useCallback(async () => {
    const uid = currentUser?.id;
    if (!uid) {
      setUnreadCount(0);
      return;
    }
    const { count, error } = await supabase
      .from('alerts')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', uid)
      .eq('is_read', false);
    if (error) {
      setUnreadCount(0);
      return;
    }
    setUnreadCount(count ?? 0);
  }, [currentUser?.id]);

  useEffect(() => {
    void refreshUnread();
  }, [refreshUnread, location.pathname]);

  useEffect(() => {
    const onAlertsChanged = () => {
      void refreshUnread();
    };
    window.addEventListener('shieldsme-alerts-changed', onAlertsChanged);
    return () => window.removeEventListener('shieldsme-alerts-changed', onAlertsChanged);
  }, [refreshUnread]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const handleSignOut = async () => {
    setMenuOpen(false);
    clearCart();
    await signOut();
    navigate('/login', { replace: true });
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive ? 'bg-accent/15 text-accent' : 'text-muted hover:bg-foreground/5 hover:text-foreground'
    }`;

  const mobileNavClass = ({ isActive }: { isActive: boolean }) =>
    `flex flex-1 flex-col items-center gap-1 py-2 text-xs font-medium ${
      isActive ? 'text-accent' : 'text-muted'
    }`;

  const sidebarName = isPersonal
    ? userProfile?.owner_name?.trim() || currentUser?.email || ''
    : userProfile?.business_name?.trim() || currentUser?.email || '';

  const scheduleIso = deletionScheduledAt ?? '';
  const modalDateLabel = scheduleIso ? formatDeletionDisplayDate(scheduleIso) : 'the scheduled date';
  const modalDays = scheduleIso ? calendarDaysUntilDeletion(scheduleIso) : 0;
  const modalWhenPhrase =
    modalDays <= 0 ? 'today' : modalDays === 1 ? 'tomorrow' : `in ${modalDays} days`;

  return (
    <div className="flex min-h-screen flex-col bg-navy">
      {!authLoading && currentUser && userProfile !== null && !isOnboardingComplete ? (
        <OnboardingWizard
          onComplete={async (info?: OnboardingCompleteInfo) => {
            if (info?.assetWarnings?.length) {
              const body = [...info.assetWarnings, 'You can add assets from the Assets page.'].join(' ');
              pushToast(body, 'error');
            }
            await refreshProfile();
          }}
        />
      ) : null}
      {fixedBannerRowCount > 0 ? (
        <div className="fixed left-0 right-0 top-0 z-[150] flex flex-col shadow-md">
          {showTrialBanner ? (
            <div
              className="flex min-h-[44px] w-full items-center justify-center gap-2 px-3 py-2.5 text-center text-sm font-medium"
              style={trialBannerStyle}
              role="status"
            >
              <span>
                🛡 You&apos;re on a 14-day free {trialPlanLabel} trial — {trialDaysLeft}{' '}
                {trialDaysLeft === 1 ? 'day' : 'days'} remaining.{` `}
              </span>
              <Link
                to="/marketplace"
                className="whitespace-nowrap font-semibold underline decoration-current underline-offset-2 transition hover:opacity-90"
              >
                Upgrade now →
              </Link>
              <button
                type="button"
                onClick={handleDismissTrialBanner}
                className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded text-base leading-none transition hover:bg-black/10"
                aria-label="Dismiss trial banner"
                title="Dismiss"
              >
                ×
              </button>
            </div>
          ) : null}
          {showGraceBanner ? (
            <div
              className="flex min-h-[44px] w-full shrink-0 items-center justify-center gap-2 px-3 py-2.5 text-center text-sm font-medium"
              style={graceBannerStyle}
              role="status"
            >
              <span>
                ⚠️ Your trial expired {trialExpiredDaysAgo}{' '}
                {trialExpiredDaysAgo === 1 ? 'day' : 'days'} ago — you have {graceDaysLeft}{' '}
                {graceDaysLeft === 1 ? 'day' : 'days'} left to upgrade before monitoring pauses.{` `}
              </span>
              <Link
                to="/marketplace"
                className="whitespace-nowrap font-semibold underline decoration-current underline-offset-2 transition hover:opacity-90"
              >
                Upgrade now →
              </Link>
              <button
                type="button"
                onClick={handleDismissGraceBanner}
                className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded text-base leading-none transition hover:bg-black/10"
                aria-label="Dismiss grace period notice"
                title="Dismiss"
              >
                ×
              </button>
            </div>
          ) : null}
          {showSoftStopBanner ? (
            <div
              className="flex min-h-[44px] w-full shrink-0 items-center justify-center gap-2 px-3 py-2.5 text-center text-sm font-medium text-white"
              style={{ backgroundColor: '#B91C1C' }}
              role="alert"
            >
              <span>
                🔴 Monitoring paused — {softStopThreatCount}{' '}
                {softStopThreatCount === 1 ? 'threat may' : 'threats may'} be undetected. Upgrade now to restore full
                protection.{` `}
              </span>
              <Link
                to="/marketplace"
                className="whitespace-nowrap font-semibold underline decoration-white/90 underline-offset-2 transition hover:opacity-90"
              >
                Upgrade now →
              </Link>
            </div>
          ) : null}
          {showExpiredAssetTrialBanner ? (
            <div
              className="flex min-h-[44px] w-full shrink-0 items-center justify-center gap-2 px-3 py-2.5 text-center text-sm font-medium text-white"
              style={{ backgroundColor: '#DC2626' }}
              role="status"
            >
              <span>{assetTrialExpiryBannerMessage}</span>
              <Link
                to="/marketplace"
                className="whitespace-nowrap font-semibold underline decoration-white/90 underline-offset-2 transition hover:opacity-90"
              >
                Upgrade now →
              </Link>
              <button
                type="button"
                onClick={handleDismissTrialExpiryAssetBanner}
                className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded text-base leading-none transition hover:bg-black/10"
                aria-label="Dismiss expired trial notice"
                title="Dismiss"
              >
                ×
              </button>
            </div>
          ) : null}
          {showDeletionBanner ? (
            <div
              className="flex min-h-[44px] w-full shrink-0 items-center justify-center gap-1 px-3 text-center text-sm font-medium text-white"
              style={{ backgroundColor: '#DC2626' }}
              role="status"
            >
              <span>
                ⚠️{' '}
                {scheduleIso
                  ? deletionBannerCopy(scheduleIso)
                  : 'Your account is scheduled for permanent deletion. '}
              </span>
              <button
                type="button"
                disabled={bannerCancelLoading}
                onClick={() => void handleDeletionCancel('banner')}
                className="whitespace-nowrap underline decoration-white/90 underline-offset-2 transition hover:opacity-90 disabled:opacity-60"
              >
                {bannerCancelLoading ? 'Cancelling…' : 'Cancel deletion →'}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        style={{
          paddingTop: fixedBannerRowCount > 0 ? fixedBannerRowCount * 44 : 0,
        }}
      >
        <div className="flex min-h-0 flex-1">
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-border bg-surface/80 backdrop-blur-sm lg:flex">
          <div className="border-b border-border px-5 py-6">
            <Link to="/dashboard" className="inline-flex items-center gap-2">
              <Logo size="md" clickable={false} />
            </Link>
          </div>

          <nav className="flex flex-1 flex-col gap-1 px-3 py-4" aria-label="Main">
            <NavLink to="/dashboard" end className={navClass}>
              <IconHome className="h-5 w-5 shrink-0" />
              <span>Dashboard</span>
            </NavLink>
            <NavLink to="/alerts" className={navClass}>
              <span className="relative inline-flex shrink-0">
                <IconBell className="h-5 w-5" />
                {unreadCount > 0 ? (
                  <span className="absolute -right-2 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                ) : null}
              </span>
              <span>Alerts</span>
            </NavLink>
            <NavLink to="/assets" className={navClass}>
              <IconAssets className="h-5 w-5 shrink-0" />
              <span>Assets</span>
            </NavLink>
            <NavLink to="/download" className={navClass}>
              <IconDownload className="h-5 w-5 shrink-0" />
              <span>Download Agent</span>
            </NavLink>
            <FeatureGate feature="momoProtection">
              <NavLink to="/momo" className={navClass}>
                <IconMomoShield className="h-5 w-5 shrink-0" />
                <span>MoMo Protection</span>
              </NavLink>
            </FeatureGate>
            <NavLink to="/network" className={navClass}>
              <IconNetworkNav className="h-5 w-5 shrink-0" />
              <span>Network</span>
            </NavLink>
            <NavLink to="/reports" className={navClass}>
              <IconDocument className="h-5 w-5 shrink-0" />
              <span>Reports</span>
            </NavLink>
            <NavLink to="/marketplace" className={navClass}>
              <span className="relative inline-flex shrink-0">
                <IconMarketplaceBag className="h-5 w-5" />
                {totalItems > 0 ? (
                  <span className="absolute -right-2 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-cta-text">
                    {totalItems > 99 ? '99+' : totalItems}
                  </span>
                ) : null}
              </span>
              <span>Marketplace</span>
            </NavLink>
            <NavLink to="/settings" className={navClass}>
              <IconCog className="h-5 w-5 shrink-0" />
              <span>Settings</span>
            </NavLink>
          </nav>

          <div className="mt-auto border-t border-border px-4 py-5">
            <div className="flex items-center gap-3">
              <AvatarCircle url={avatarUrl} initials={initials} className="h-10 w-10 text-sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{sidebarName || 'Account'}</p>
                <button
                  type="button"
                  onClick={() => void handleSignOut()}
                  className="text-xs font-medium text-muted underline-offset-2 hover:text-accent hover:underline"
                >
                  Log Out
                </button>
              </div>
            </div>
          </div>
        </aside>

        <div className="flex min-h-screen min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-40 flex items-center justify-between gap-4 border-b border-border bg-navy/95 px-4 py-3 backdrop-blur-md sm:px-6">
            <h1 className="truncate text-lg font-semibold tracking-tight text-foreground sm:text-xl">
              {title}
            </h1>
            <div className="flex items-center gap-2 sm:gap-3">
              <NavBarTooltip label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                <ThemeToggle />
              </NavBarTooltip>
              <NavBarTooltip label="View notifications">
                <Link
                  to="/alerts"
                  className="relative rounded-lg p-2 text-muted transition-colors hover:bg-foreground/5 hover:text-foreground"
                  aria-label="Notifications"
                >
                  <IconBell className="h-6 w-6" />
                  {unreadCount > 0 ? (
                    <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-accent ring-2 ring-navy" />
                  ) : null}
                </Link>
              </NavBarTooltip>
              <NavBarTooltip label="View cart">
                <Link
                  to="/marketplace"
                  className="relative rounded-lg p-2 text-muted transition-colors hover:bg-foreground/5 hover:text-foreground"
                  aria-label="Shopping cart"
                >
                  <IconMarketplaceBag className="h-6 w-6" />
                  {totalItems > 0 ? (
                    <span className="absolute right-1 top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-cta-text">
                      {totalItems > 99 ? '99+' : totalItems}
                    </span>
                  ) : null}
                </Link>
              </NavBarTooltip>
              <div className="relative" ref={menuRef}>
                <NavBarTooltip label="Account menu">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen((o) => !o);
                    }}
                    className="rounded-full ring-2 ring-transparent transition ring-offset-2 ring-offset-navy hover:ring-accent/40"
                    aria-expanded={menuOpen}
                    aria-haspopup="menu"
                    aria-label="Account menu"
                  >
                    <AvatarCircle url={avatarUrl} initials={initials} />
                  </button>
                </NavBarTooltip>
                {menuOpen ? (
                  <div
                    role="menu"
                    className="absolute right-0 top-full z-50 mt-2 w-48 rounded-lg border border-border bg-surface py-1 shadow-xl"
                  >
                    <Link
                      role="menuitem"
                      to="/settings"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-foreground/5"
                      onClick={() => setMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <Link
                      role="menuitem"
                      to="/settings"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-foreground/5"
                      onClick={() => setMenuOpen(false)}
                    >
                      Settings
                    </Link>
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full px-4 py-2 text-left text-sm text-danger hover:bg-foreground/5"
                      onClick={() => void handleSignOut()}
                    >
                      Log Out
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </header>

          <main
            id="main-content-root"
            className="relative min-h-0 flex-1 overflow-x-hidden px-4 py-6 sm:px-6 lg:px-8 pb-24 lg:pb-10"
          >
            <Outlet context={outletBillingContext} />
          </main>

          <nav
            className="fixed bottom-0 left-0 right-0 z-40 flex border-t border-border bg-surface/95 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur-md lg:hidden"
            aria-label="Mobile"
          >
            <NavLink to="/dashboard" end className={mobileNavClass}>
              <IconHome className="h-6 w-6" />
              <span>Dashboard</span>
            </NavLink>
            <NavLink to="/alerts" className={mobileNavClass}>
              <span className="relative">
                <IconBell className="h-6 w-6" />
                {unreadCount > 0 ? (
                  <span className="absolute -right-2 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                ) : null}
              </span>
              <span>Alerts</span>
            </NavLink>
            <NavLink to="/assets" className={mobileNavClass}>
              <IconAssets className="h-6 w-6" />
              <span>Assets</span>
            </NavLink>
            <NavLink to="/download" className={mobileNavClass}>
              <IconDownload className="h-6 w-6" />
              <span>Download</span>
            </NavLink>
            <FeatureGate feature="momoProtection">
              <NavLink to="/momo" className={mobileNavClass}>
                <IconMomoShield className="h-6 w-6" />
                <span>MoMo</span>
              </NavLink>
            </FeatureGate>
            <NavLink to="/network" className={mobileNavClass}>
              <IconNetworkNav className="h-6 w-6" />
              <span>Network</span>
            </NavLink>
            <NavLink to="/reports" className={mobileNavClass}>
              <IconDocument className="h-6 w-6" />
              <span>Reports</span>
            </NavLink>
            <NavLink to="/marketplace" className={mobileNavClass}>
              <span className="relative inline-flex">
                <IconMarketplaceBag className="h-6 w-6" />
                {totalItems > 0 ? (
                  <span className="absolute -right-2 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-cta-text">
                    {totalItems > 99 ? '99+' : totalItems}
                  </span>
                ) : null}
              </span>
              <span>Marketplace</span>
            </NavLink>
            <NavLink to="/settings" className={mobileNavClass}>
              <IconCog className="h-6 w-6" />
              <span>Settings</span>
            </NavLink>
          </nav>
        </div>
        </div>
      </div>

      {showDeletionBlockerModal ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="deletion-modal-title"
          aria-describedby="deletion-modal-desc"
        >
          <div className="w-full max-w-[480px] overflow-hidden rounded-xl border border-border bg-surface shadow-2xl">
            <div
              className="flex items-center gap-2 border-b border-border px-4 py-3 text-white"
              style={{ backgroundColor: '#DC2626' }}
            >
              <span className="text-lg" aria-hidden>
                ⚠️
              </span>
              <h2 id="deletion-modal-title" className="text-base font-semibold">
                Your Account Is Scheduled for Deletion
              </h2>
            </div>
            <div id="deletion-modal-desc" className="space-y-4 px-4 py-5 text-sm text-foreground">
              <p>
                Your account will be permanently deleted on <strong>{modalDateLabel}</strong>
                {modalDays <= 0 || modalDays === 1 ? (
                  <>
                    {' '}
                    (<strong>{modalWhenPhrase}</strong>).
                  </>
                ) : (
                  <>
                    {' '}
                    — <strong>{modalWhenPhrase}</strong>.
                  </>
                )}
              </p>
              <p className="text-muted">
                After this date, all your data will be permanently destroyed and cannot be recovered.
              </p>
              <p className="font-medium text-foreground">What would you like to do?</p>
              {deletionModalError ? (
                <p className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
                  {deletionModalError}
                </p>
              ) : null}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                <button
                  type="button"
                  disabled={modalCancelLoading}
                  onClick={() => void handleDeletionCancel('modal')}
                  className="flex-1 rounded-lg px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: '#00a86b' }}
                >
                  {modalCancelLoading ? 'Cancelling…' : 'Cancel Deletion (Recommended)'}
                </button>
                <button
                  type="button"
                  disabled={modalCancelLoading}
                  onClick={handleDeletionAcknowledge}
                  className="flex-1 rounded-lg border border-border px-4 py-3 text-sm font-semibold transition hover:bg-foreground/5 disabled:opacity-60"
                  style={{ color: '#6B7280' }}
                >
                  I understand, continue
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <SessionTimeoutModal
        open={showTimeoutWarning}
        secondsRemaining={secondsRemaining}
        onStayLoggedIn={handleStayLoggedIn}
        onLogoutNow={handleLogoutNowFromModal}
      />

      <div className="pointer-events-none fixed bottom-6 left-0 right-0 z-[250] flex flex-col items-center gap-2 px-4">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto max-w-md rounded-lg border px-4 py-3 text-sm font-medium shadow-lg ${
              t.variant === 'success'
                ? 'border-accent/40 bg-surface text-accent'
                : 'border-danger/40 bg-surface text-danger'
            }`}
            role="status"
          >
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}
